/**
 * Role-aware API factory for dynamic endpoint resolution
 * Automatically selects endpoints based on user role (admin/staff)
 */

// Internal dependencies
import { apiClient } from "./apiClient"
import { useStore } from "@/store"

/**
 * Role-based endpoint configuration
 */
export interface RoleEndpoints {
  user: string // Admin endpoint
  staff: string // Staff portal endpoint
}

/**
 * Module endpoint configuration
 * Can be either a single endpoint (same for both roles) or role-specific endpoints
 */
export type EndpointConfig = string | RoleEndpoints

/**
 * Configuration for a module's API endpoints
 */
export interface ModuleEndpoints {
  base: EndpointConfig
  getAll?: EndpointConfig
  getById?: EndpointConfig
  create?: EndpointConfig
  update?: EndpointConfig
  delete?: EndpointConfig
  [customAction: string]: EndpointConfig | undefined
}

/**
 * Resolves an endpoint based on current user role
 */
export const resolveEndpoint = (config: EndpointConfig): string => {
  if (typeof config === "string") {
    return config
  }

  const { isStaff } = useStore.getState()
  return isStaff ? config.staff : config.user
}

/**
 * Builds endpoint path with ID if provided
 */
const buildPath = (base: string, id?: string, action?: string): string => {
  let path = base
  if (id) path += `/${id}`
  if (action) path += `/${action}`
  return path
}

/**
 * Creates a role-aware API service for a module
 * 
 */
export const createRoleAwareApi = (endpoints: ModuleEndpoints) => {
  const getEndpoint = (action?: keyof ModuleEndpoints) => {
    const config = action && endpoints[action] ? endpoints[action]! : endpoints.base
    return resolveEndpoint(config)
  }

  return {
    /**
     * GET /endpoint (with optional query params)
     */
    getAll: async (params?: Record<string, any>): Promise<any> => {
      const endpoint = getEndpoint("getAll")
      const response = await apiClient.get(endpoint, { params })
      return response.data
    },

    /**
     * GET /endpoint/:id
     */
    getById: async (id: string, params?: Record<string, any>): Promise<any> => {
      const endpoint = getEndpoint("getById")
      const path = buildPath(endpoint, id)
      const response = await apiClient.get(path, { params })
      return response.data
    },

    /**
     * POST /endpoint
     */
    create: async (data: any): Promise<any> => {
      const endpoint = getEndpoint("create")
      const response = await apiClient.post(endpoint, data)
      return response.data
    },

    /**
     * PUT /endpoint/:id
     */
    update: async (id: string, data: any): Promise<any> => {
      const endpoint = getEndpoint("update")
      const path = buildPath(endpoint, id)
      const response = await apiClient.put(path, data)
      return response.data
    },

    /**
     * PATCH /endpoint/:id
     */
    patch: async (id: string, data: any): Promise<any> => {
      const endpoint = getEndpoint("update")
      const path = buildPath(endpoint, id)
      const response = await apiClient.patch(path, data)
      return response.data
    },

    /**
     * DELETE /endpoint/:id
     */
    delete: async (id: string): Promise<any> => {
      const endpoint = getEndpoint("delete")
      const path = buildPath(endpoint, id)
      const response = await apiClient.delete(path)
      return response.data
    },

    /**
     * Custom action: POST /endpoint/:id/:action
     */
    customAction: async (
      action: string,
      id?: string,
      data?: any,
      method: "GET" | "POST" | "PATCH" | "PUT" = "POST"
    ): Promise<any> => {
      const customEndpoint = endpoints[action]
      const endpoint = customEndpoint ? resolveEndpoint(customEndpoint) : getEndpoint()
      const path = buildPath(endpoint, id, action)

      const requestConfig = method === "GET" ? { params: data } : { data }

      const response = await apiClient[method.toLowerCase() as Lowercase<typeof method>](
        path,
        method === "GET" ? requestConfig : data
      )
      return response.data
    },
  }
}

/**
 * Helper to create role-specific endpoint config
 */
export const roleEndpoints = (userPath: string, staffPath: string): RoleEndpoints => ({
  user: userPath,
  staff: staffPath,
})

